import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/store/hooks';
import {
  fetchWebsiteServices,
  setServicesQuery,
  updateWebsiteService,
  deleteWebsiteService,
  bulkDeleteWebsiteServices,
  toggleServiceSelected,
  setServicesSelected,
} from '../../redux/features/websiteSlice';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { toast } from 'sonner';
import {
  Tags,
  Loader2,
  AlertCircle,
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  Power,
  PowerOff,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export function WebsiteServices() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { items: services, meta, loading, error } = useAppSelector(
    (state) => state.website.services,
  );
  const query = useAppSelector((state) => state.website.servicesQuery);
  const selectedIds = useAppSelector((state) => state.website.selectedServices);

  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [deletingOne, setDeletingOne] = useState(false);

  // Hydrate the query from the URL once so filters/pagination are deep-linkable.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    dispatch(
      setServicesQuery({
        page: Math.max(1, parseInt(params.get('page') || '1', 10)),
        search: params.get('search') || '',
        status: params.get('status') || '',
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the URL in sync with the Redux query state.
  useEffect(() => {
    const next = new URLSearchParams();
    if (query.page > 1) next.set('page', String(query.page));
    if (query.search) next.set('search', query.search);
    if (query.status) next.set('status', query.status);
    navigate(`${location.pathname}${next.toString() ? `?${next}` : ''}`, { replace: true });
  }, [query.page, query.search, query.status, navigate, location.pathname]);

  // API-based fetch (server does filtering + pagination; Redis caches the response).
  // Debounced so rapid typing collapses into a single request.
  useEffect(() => {
    const timer = setTimeout(() => dispatch(fetchWebsiteServices(query)), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, query.page, query.limit, query.search, query.status]);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    const visible = new Set(services.map((svc) => svc.id));
    const filtered = selectedIds.filter((id) => visible.has(id));
    if (filtered.length !== selectedIds.length) dispatch(setServicesSelected(filtered));
  }, [services, selectedIds, dispatch]);

  const allSelected = services.length > 0 && services.every((svc) => selectedIds.includes(svc.id));
  const someSelected = services.some((svc) => selectedIds.includes(svc.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    const everySelected = services.length > 0 && services.every((svc) => selectedIds.includes(svc.id));
    dispatch(setServicesSelected(everySelected ? [] : services.map((svc) => svc.id)));
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      await dispatch(updateWebsiteService({ id, data: { isActive: !isActive } })).unwrap();
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to update service');
    }
  };

  const handleDeleteOne = async () => {
    if (!deleteTarget) return;
    setDeletingOne(true);
    try {
      await dispatch(deleteWebsiteService(deleteTarget.id)).unwrap();
      toast.success('Service deleted');
      dispatch(fetchWebsiteServices(query));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to delete service');
    } finally {
      setDeletingOne(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    setBulkDeleting(true);
    try {
      await dispatch(bulkDeleteWebsiteServices(ids)).unwrap();
      toast.success(`Deleted ${ids.length} service${ids.length === 1 ? '' : 's'}`);
      dispatch(fetchWebsiteServices(query));
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to delete selected services');
    } finally {
      setBulkDeleting(false);
      setBulkConfirmOpen(false);
    }
  };

  const hasFilters = !!query.search || !!query.status;
  const isInitialLoading = loading && services.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Tags className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-text">Website Services</h1>
            <p className="text-sm text-text-muted">Services listed on the public /services page.</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/website/services/new')}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          Add Service
        </button>
      </div>

      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search services..."
              value={query.search}
              onChange={(e) => dispatch(setServicesQuery({ search: e.target.value, page: 1 }))}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {query.search && (
              <button
                onClick={() => dispatch(setServicesQuery({ search: '', page: 1 }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <select
            value={query.status}
            onChange={(e) => dispatch(setServicesQuery({ status: e.target.value, page: 1 }))}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 sm:w-48"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleting}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
            <button
              type="button"
              onClick={() => dispatch(setServicesSelected([]))}
              disabled={bulkDeleting}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {isInitialLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error && services.length === 0 ? (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          {loading && (
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border text-xs text-text-muted">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Updating…
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={services.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Service</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Slug</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Count</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {services.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                      {hasFilters ? (
                        <span>
                          No services match your filters.{' '}
                          <button
                            onClick={() =>
                              dispatch(setServicesQuery({ search: '', status: '', page: 1 }))
                            }
                            className="text-primary hover:underline"
                          >
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        'No website services yet. Add your first service to get started.'
                      )}
                    </td>
                  </tr>
                ) : (
                  services.map((svc) => (
                    <tr key={svc.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(svc.id)}
                          onChange={() => dispatch(toggleServiceSelected(svc.id))}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {svc.icon ? (
                            <img
                              src={svc.icon}
                              alt=""
                              className="w-9 h-9 rounded-lg object-contain bg-background border border-border flex-shrink-0"
                            />
                          ) : (
                            <span className="w-9 h-9 rounded-lg bg-background border border-border" />
                          )}
                          <div className="min-w-0">
                            <button
                              onClick={() => navigate(`/website/services/${svc.id}/edit`)}
                              className="text-sm font-medium text-text hover:text-primary text-left truncate block max-w-[280px]"
                            >
                              {svc.title}
                            </button>
                            <p className="text-xs text-text-muted truncate max-w-[280px]">
                              {svc.description}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-text-muted">{svc.slug}</td>
                      <td className="px-4 py-3 text-sm text-text">{svc.count}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            svc.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                          }`}
                        >
                          {svc.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleToggleActive(svc.id, svc.isActive)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title={svc.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {svc.isActive ? <PowerOff size={16} /> : <Power size={16} />}
                          </button>
                          <button
                            onClick={() => navigate(`/website/services/${svc.id}/edit`)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget({ id: svc.id, title: svc.title })}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={query.page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={query.limit}
            onPageChange={(page) => dispatch(setServicesQuery({ page }))}
            itemName="services"
          />
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete service?"
        description={
          <>
            This will delete <strong className="text-text">{deleteTarget?.title}</strong> from the
            website. This cannot be undone.
          </>
        }
        confirmLabel="Delete service"
        loading={deletingOne}
        onConfirm={handleDeleteOne}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.length} service${selectedIds.length === 1 ? '' : 's'}?`}
        description="This action will remove the selected services. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.length} service${selectedIds.length === 1 ? '' : 's'}`}
        loading={bulkDeleting}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
